/* eslint-disable react/require-default-props */
import {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Form as FormLib, FormInstance, FormProps, Input, Select } from 'antd';
import { OptionProps } from 'antd/es/select';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Field, FieldBase, FieldType } from '../../types';
import StringUtils from '../../utils';
import { RefFormHandler } from '../../pages/orders/details';
import { Button, ButtonProps } from '../inputs/button';
import FormDateTimePicker from './FormDateTimePicker';
import FormFile from './FormFile';
import FormFileUpload from './FormFileUpload';
import DatePicker from '../DatePicker';

/**
 * Form props
 */
interface Props {
  fields: Field[];
  formProps?: FormProps;
  hasSubmitButton?: boolean;
  defaultValues?: any;
  submitButtonProps?: Partial<ButtonProps>;
  className?: string;
  fieldOptions?: Partial<Omit<FieldBase, 'key'>>;
}

/**
 * Form Component
 */
const Form: ForwardRefRenderFunction<RefFormHandler, Props> = (
  {
    fields,
    formProps = {},
    hasSubmitButton = true,
    defaultValues = {},
    submitButtonProps,
    className,
    fieldOptions,
  },
  ref,
) => {
  type Type = Record<typeof fields[number]['key'], string>;
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    reset,
    watch,
  } = useForm<Type>({ defaultValues });

  const formRef = useRef<FormInstance>(null);

  const onSubmitCapture = useMemo(() => {
    if (formProps?.onSubmitCapture) {
      return handleSubmit(
        formProps.onSubmitCapture as unknown as (data: Type) => void,
      );
    }
    return undefined;
  }, [formProps.onSubmitCapture, handleSubmit]);

  const onSubmit = useCallback(async () => {
    if (onSubmitCapture) {
      onSubmitCapture();
    }
  }, [onSubmitCapture]);

  useImperativeHandle(ref, () => ({
    onSubmit,
    getValues,
    watch,
    reset,
  }));

  const label = useCallback(
    (field: Field): string =>
      (field.label || StringUtils.prettify(field.key)) +
      (field.required ? '*' : ''),
    [],
  );

  const renderField = useCallback(
    (field: Field) => {
      if (field.type === FieldType.Divider) {
        return <hr key={field.key} className="mb-[24px]" />;
      }

      const placeholder: string = label(field);
      const error: FieldError | undefined = errors[field.key];
      const hasLabel = field.hasLabel !== false;
      return (
        <div
          className={`basis-full mb-[24px] px-1 ${
            fieldOptions?.className || ''
          } ${field.className || ''}`}
          key={field.key}>
          <FormLib.Item
            label={hasLabel ? label(field) : undefined}
            name={field.key}
            className="form-item mb-0"
            key={field.key}
            validateStatus={error ? 'error' : undefined}
            help={
              error?.message && <pre className="text-xs">{error?.message}</pre>
            }
            {...field.itemProps}>
            <Controller
              control={control}
              rules={{
                required: {
                  value: Boolean(field.required),
                  message: 'This field is required.',
                },
                ...(field.rules || {}),
              }}
              render={({ field: { onChange: cOnChange, onBlur, value } }) => {
                const onChange = (...args: any[]) => {
                  if (cOnChange) {
                    cOnChange(...args);
                  }
                  if (field.onChange) {
                    field.onChange(watch(field.key));
                  }
                };
                if (field.type === FieldType.Select) {
                  return (
                    <Select
                      style={{ width: '100%' }}
                      placeholder={placeholder}
                      disabled={field.disabled}
                      {...field.inputProps}
                      {...{ onChange, onBlur, value }}>
                      {field.options.map(option => (
                        <Select.Option
                          key={option.value}
                          {...(option as OptionProps)}>
                          {option?.label || option?.value}
                        </Select.Option>
                      ))}
                    </Select>
                  );
                }
                if (field.type === FieldType.DateAndTime) {
                  return (
                    <FormDateTimePicker
                      value={value ? new Date(value) : undefined}
                      onChange={onChange}
                      onBlur={onBlur}
                      disabled={field.disabled}
                    />
                  );
                }
                if (field.type === FieldType.Date) {
                  return (
                    <div className="w-full">
                      <DatePicker
                        value={value ? new Date(value) : undefined}
                        onChange={onChange}
                      />
                    </div>
                  );
                }
                if (field.type === FieldType.File) {
                  const props =
                    typeof field.inputProps === 'function'
                      ? field.inputProps({ value })
                      : field.inputProps;
                  return (
                    <FormFile
                      value={value}
                      onBlur={onBlur}
                      disabled={field.disabled}
                      {...props}
                      onChange={(...args) => {
                        onChange(...args);
                        // eslint-disable-next-line react/prop-types
                        if (props?.onChange) {
                          // eslint-disable-next-line react/prop-types
                          props?.onChange(...args);
                        }
                      }}
                    />
                  );
                }

                if (field.type === FieldType.FileUpload) {
                  return <FormFileUpload />;
                }

                if (field.type === FieldType.Custom) {
                  return field.render({ onBlur, onChange, value });
                }

                if (field.type === FieldType.TextArea) {
                  return (
                    <Input.TextArea
                      disabled={field.disabled}
                      placeholder={placeholder}
                      {...field.inputProps}
                      style={{ minHeight: 100, maxHeight: 350 }}
                      {...{ onChange, onBlur, value }}
                    />
                  );
                }
                return (
                  <Input
                    disabled={field.disabled}
                    {...field.inputProps}
                    placeholder={placeholder}
                    type={
                      field.type === FieldType.Password ? 'password' : 'text'
                    }
                    {...{ onChange, onBlur, value }}
                  />
                );
              }}
              name={field.key}
            />
          </FormLib.Item>
        </div>
      );
    },
    [control, errors, fieldOptions?.className, label, watch],
  );

  useEffect(() => {
    Object.keys(defaultValues).forEach(key => {
      setValue(key, defaultValues[key]);
    });
  }, [defaultValues, setValue]);

  return (
    <FormLib
      ref={formRef}
      layout="vertical"
      {...formProps}
      onSubmitCapture={onSubmitCapture}
      className={`flex flex-col ${className}`}>
      <div className="flex flex-1 flex-col md:flex-row flex-wrap">
        {fields.map(renderField)}
      </div>
      {hasSubmitButton ? (
        <Button type="submit" {...submitButtonProps}>
          Submit
        </Button>
      ) : null}
    </FormLib>
  );
};

export default forwardRef(Form);
